import { NotificationPresentationPipe } from './components/notifications/notification-presentation.pipe';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { TestFirestoreComponent } from './components/test-firestore/test-firestore.component';

import { environment } from '../environments/environment';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { provideFirestore } from '@angular/fire/firestore';
import { provideAuth } from '@angular/fire/auth';
import { provideStorage } from '@angular/fire/storage';
import { getApp } from 'firebase/app';
import { getStorage } from 'firebase/storage';
import {
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  indexedDBLocalPersistence,
  initializeRecaptchaConfig,
  setPersistence
} from 'firebase/auth';
import { connectFirestoreEmulator, initializeFirestore, persistentLocalCache, persistentMultipleTabManager } from 'firebase/firestore';
import { HomeComponent } from './components/home/home.component';
import { SharedComponent } from './components/shared/shared.component';
import { HeaderComponent } from './components/header/header.component';
import { TimestampToDatePipe } from './timestamp-to-date.pipe';
import { FooterComponent } from './components/shared/footer/footer.component';
import { ContactComponent } from './components/contact/contact.component';
import { PrivacyPolicyComponent } from './components/privacy-policy/privacy-policy.component';
import { TermsAndConditionsComponent } from './components/terms-and-conditions/terms-and-conditions.component';
import { RegisterComponent } from './components/auth/register/register.component';
import { LoginComponent } from './components/auth/login/login.component';
import { PasswordResetComponent } from './components/auth/password-reset/password-reset.component';
import { VerifyEmailComponent } from './components/auth/verify-email/verify-email.component';
import { AboutComponent } from './components/about/about.component';
import { HowItWorksComponent } from './components/how-it-works/how-it-works.component';
import { FeaturesComponent } from './components/features/features.component';
import { SubscriptionsComponent } from './components/subscriptions/subscriptions.component';
import { CheckoutComponent } from './components/checkout/checkout.component';
import { SubscriptionConfirmationComponent } from './components/subscription-confirmation/subscription-confirmation.component';
import { ManageSubscriptionComponent } from './components/manage-subscription/manage-subscription.component';
import { StarterComponent } from './components/starter/starter.component';
import { SmallFleetComponent } from './components/small-fleet/small-fleet.component';
import { MidFleetComponent } from './components/mid-fleet/mid-fleet.component';
import { LargeFleetComponent } from './components/large-fleet/large-fleet.component';
import { UpgradeSubscriptionComponent } from './components/upgrade-subscription/upgrade-subscription.component';
import { DaysUntilDatePipe } from './days-until-date.pipe';
import { DaysSinceDatePipe } from './days-since-date.pipe';
import { SpinnerComponent } from './components/spinner/spinner.component';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { LoadingInterceptor } from './interceptors/loading.interceptor';
import { AuthTokenInterceptor } from './interceptors/auth-token.interceptor';
import { UnauthorizedComponent } from './components/unauthorized/unauthorized.component';
import { UnknownComponent } from './components/unknown/unknown.component';
import { ToastComponent } from './components/toast/toast.component';
import { ImportantNotificationModalComponent } from './components/notifications/important-notification-modal/important-notification-modal.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { DocumentViewerComponent } from './components/document-viewer/document-viewer.component';
import { AcceptInviteComponent } from './components/accounts/accept-invite/accept-invite.component';
import { ServiceBookShareViewComponent } from './components/service-book/service-book-share-view.component';
import { CookieConsentModalComponent } from './components/cookie-consent-modal/cookie-consent-modal.component';
import { AppDisabledComponent } from './components/app-disabled/app-disabled.component';
import { SharedUiModule } from './shared-ui.module';

type FirebaseEmulatorTarget = { host: string; port: number };
type FirebaseEmulatorsConfig = {
  auth?: FirebaseEmulatorTarget;
  firestore?: FirebaseEmulatorTarget;
};

let firestoreEmulatorConnected = false;
let authEmulatorConnected = false;

function getFirebaseEmulatorsConfig(): FirebaseEmulatorsConfig | null {
  return (environment as typeof environment & { firebaseEmulators?: FirebaseEmulatorsConfig | null }).firebaseEmulators ?? null;
}

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    TestFirestoreComponent,
    SharedComponent,
    HeaderComponent,
    FooterComponent,
    ContactComponent,
    PrivacyPolicyComponent,
    TermsAndConditionsComponent,
    RegisterComponent,
    LoginComponent,
    PasswordResetComponent,
    VerifyEmailComponent,
    AboutComponent,
    HowItWorksComponent,
    FeaturesComponent,
    SubscriptionsComponent,
    CheckoutComponent,
    SubscriptionConfirmationComponent,
    ManageSubscriptionComponent,
    StarterComponent,
    SmallFleetComponent,
    MidFleetComponent,
    LargeFleetComponent,
    UpgradeSubscriptionComponent,
    SpinnerComponent,
    UnauthorizedComponent,
    UnknownComponent,
    ToastComponent,
    ImportantNotificationModalComponent,
    SidebarComponent,
    DocumentViewerComponent,
    AcceptInviteComponent,
    ServiceBookShareViewComponent,
    CookieConsentModalComponent,
    AppDisabledComponent,
  ],
  imports: [
    NotificationPresentationPipe,
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    TimestampToDatePipe,
    DaysSinceDatePipe,
    DaysUntilDatePipe,
    AppRoutingModule,
    HttpClientModule,
    SharedUiModule
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthTokenInterceptor, multi: true },
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideStorage(() => getStorage(getApp())),
    provideFirestore(() => {
      const app = getApp();
      const firestore = initializeFirestore(app, {
        localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() })
      });
      const emulatorConfig = getFirebaseEmulatorsConfig()?.firestore;
      if (emulatorConfig && !firestoreEmulatorConnected) {
        connectFirestoreEmulator(firestore, emulatorConfig.host, emulatorConfig.port);
        firestoreEmulatorConnected = true;
      }
      return firestore;
    }),
    provideAuth(() => {
      const auth = getAuth(getApp());
      const emulatorConfig = getFirebaseEmulatorsConfig()?.auth;
      if (emulatorConfig && !authEmulatorConnected) {
        connectAuthEmulator(auth, `http://${emulatorConfig.host}:${emulatorConfig.port}`, { disableWarnings: true });
        authEmulatorConnected = true;
      }
      if (!emulatorConfig) {
        // Firebase Auth owns the reCAPTCHA Enterprise key and token lifecycle. Fetch
        // its project configuration up front so protected auth calls avoid the
        // additional network round trip; no site key or Google script is needed here.
        void initializeRecaptchaConfig(auth)
          .catch(error => console.warn('Unable to initialize Firebase Auth reCAPTCHA protection.', error));
      }
      void setPersistence(auth, indexedDBLocalPersistence)
        .catch(() => setPersistence(auth, browserLocalPersistence))
        .catch(error => console.warn('Unable to set auth persistence.', error));
      return auth;
    })
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
